// Analytics Manager for tracking user interactions and SEO performance.
// GA4 itself is initialized by react-ga4 in entry-client.tsx, which installs
// window.gtag; this manager only forwards events to it.
import { isBrowser } from './isServer';

type Gtag = (...args: unknown[]) => void;

class AnalyticsManager {
  private static instance: AnalyticsManager;

  private constructor() {}

  static getInstance(): AnalyticsManager {
    if (!AnalyticsManager.instance) {
      AnalyticsManager.instance = new AnalyticsManager();
    }
    return AnalyticsManager.instance;
  }

  private gtag(): Gtag | null {
    if (!isBrowser) return null;
    const gtag = (window as unknown as { gtag?: Gtag }).gtag;
    return typeof gtag === 'function' ? gtag : null;
  }

  // Track page views
  trackPageView(page: string, title?: string): void {
    const gtag = this.gtag();
    if (gtag) {
      gtag('event', 'page_view', {
        page_path: page,
        page_title: title || document.title,
        page_location: window.location.href
      });
    }
  }

  // Track report submissions
  trackReportSubmission(restaurantName: string, severity: number): void {
    this.trackEvent('report_submission', {
      restaurant_name: restaurantName,
      severity_level: severity,
      event_category: 'engagement',
      event_label: 'food_poisoning_report'
    });
  }

  // Track search queries
  trackSearch(query: string, resultsCount: number): void {
    this.trackEvent('search', {
      search_term: query,
      results_count: resultsCount,
      event_category: 'engagement'
    });
  }

  // Track restaurant views
  trackRestaurantView(restaurantId: number, restaurantName: string): void {
    this.trackEvent('restaurant_view', {
      restaurant_id: restaurantId,
      restaurant_name: restaurantName,
      event_category: 'content'
    });
  }

  // Track language changes
  trackLanguageChange(fromLang: string, toLang: string): void {
    this.trackEvent('language_change', {
      from_language: fromLang,
      to_language: toLang,
      event_category: 'preferences'
    });
  }

  // Track external link clicks (e.g., to FASFC)
  trackExternalLink(url: string, linkText: string): void {
    this.trackEvent('external_link_click', {
      link_url: url,
      link_text: linkText,
      event_category: 'outbound'
    });
  }

  // Track guide/help page engagement
  trackGuideEngagement(guidePage: string, timeSpent?: number): void {
    this.trackEvent('guide_engagement', {
      guide_page: guidePage,
      time_spent_seconds: timeSpent,
      event_category: 'education'
    });
  }

  // Generic event tracking
  trackEvent(eventName: string, parameters?: Record<string, any>): void {
    const gtag = this.gtag();
    if (gtag) {
      gtag('event', eventName, parameters);
    }
  }

  // Track SEO-relevant user behavior
  trackBounceReduction(timeOnPage: number): void {
    if (timeOnPage > 30) { // User stayed more than 30 seconds
      this.trackEvent('engaged_session', {
        time_on_page: timeOnPage,
        event_category: 'engagement'
      });
    }
  }

  // Track conversion goals
  trackConversion(conversionType: 'report_started' | 'report_completed' | 'guide_read'): void {
    this.trackEvent('conversion', {
      conversion_type: conversionType,
      event_category: 'goals'
    });
  }
}

export default AnalyticsManager;